import axios from 'axios';
import { config } from '../config';
import {
  SendVerificationCodeRequestDTO,
  VerifyCodeRequestDTO,
  LoginRequestDTO,
  LoginResponseDTO,
  ChangePasswordRequestDTO,
  ChangePasswordResponseDTO
} from '../interfaces/AuthInterface';
import SessionService from './SessionService';
import { clearAppCache, prefetchSessionBootstrap } from './BootstrapService';

const API_URL = config.API_BASE_URL;

export const validateToken = async (token: string) => {
  try {
    const response = await axios.get(`${API_URL}/auth/validate`, { params: { token } });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to validate token: ${error}`);
  }
};

export const sendVerificationCode = async (data: SendVerificationCodeRequestDTO) => {
  try {
    const response = await axios.post(`${API_URL}/auth/send-verification-code`, data);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to send verification code: ${error}`);
  }
};

export const verifyCode = async (data: VerifyCodeRequestDTO) => {
  try {
    const response = await axios.post(`${API_URL}/auth/verify-code`, data);

    const token = response.data.token;
    if (token) {
      SessionService.clearSession();
      SessionService.setToken(token);
    }
    return response.data;
  } catch (error) {
    throw new Error(`Failed to send verification code: ${error}`);
  }
}

/**
 * Login com e-mail e senha
 */
export const login = async (data: LoginRequestDTO): Promise<LoginResponseDTO> => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, data);

    const loginResponse: LoginResponseDTO = response.data;

    // Clear previous session (sector, etc.) so new user doesn't see another user's data
    clearAppCache();
    SessionService.clearSession();

    // Only save token if password change not required
    if (loginResponse.token && !loginResponse.mustChangePassword) {
      SessionService.setToken(loginResponse.token);
      if (!loginResponse.subscriptionPending) {
        await prefetchSessionBootstrap();
      }
    }

    if (loginResponse.subscriptionPending) {
      SessionService.setSession('@app/subscription_pending', true);
    } else {
      localStorage.removeItem('@app/subscription_pending');
    }

    return loginResponse;
  } catch (error: any) {
    if (error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
      throw new Error('Não foi possível entrar. Nenhuma assinatura ativa encontrada. Entre em contato com o administrador.');
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Login failed');
  }
};

/**
 * Troca de senha
 */
export const changePassword = async (data: ChangePasswordRequestDTO): Promise<ChangePasswordResponseDTO> => {
  try {
    const response = await axios.post(`${API_URL}/auth/change-password`, data);

    const changeResponse: ChangePasswordResponseDTO = response.data;

    clearAppCache();
    SessionService.clearSession();

    // Save new token after password change
    if (changeResponse.token) {
      SessionService.setToken(changeResponse.token);
      await prefetchSessionBootstrap();
    }

    return changeResponse;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Falha ao alterar senha');
  }
};